import {Component, Inject, OnInit, Optional} from '@angular/core';
import {FormBuilder, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {RewardsAdminService} from '../../../../services/rewards-admin/rewards-admin.service';
import {UiAlertService} from '../../../../services/ui-alert/ui-alert.service';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {
  parseRewardCampaignRules,
  rewardCampaignAppliesToAllBrands,
} from '../../../../helpers/reward-campaign-rules.helper';

@Component({
  selector: 'app-superadmin-campaign-detail',
  templateUrl: './superadmin-campaign-detail.component.html',
  styleUrls: ['./superadmin-campaign-detail.component.scss'],
})
export class SuperadminCampaignDetailComponent implements OnInit {
  public campaign: any;
  public brands: any[] = [];
  public saving = false;
  public isDialog = false;

  public form = this.formBuilder.group({
    campaignName: ['', Validators.required],
    brandId: ['', Validators.required],
    startAt: ['', Validators.required],
    endAt: ['', Validators.required],
    startingBalance: ['', Validators.required],
    currentBalance: ['', Validators.required],
    lowBalance: ['', Validators.required],
    rules: [{}],
  });

  constructor(
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private rewardsAdminService: RewardsAdminService,
    private uiAlertService: UiAlertService,
    private snackBar: MatSnackBar,
    @Optional() private dialogRef?: MatDialogRef<SuperadminCampaignDetailComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) private dialogData?: { campaignId?: string; campaign?: any },
  ) {}

  ngOnInit(): void {
    this.isDialog = !!this.dialogRef;
    this.loadBrands();
    this.loadCampaign();
  }

  parseJsonValue(value: any): any {
    return parseRewardCampaignRules(value);
  }

  private normalizeDate(value: any): string {
    if (!value) {
      return '';
    }
    if (typeof value === 'string') {
      if (value.length >= 10 && value[4] === '-' && value[7] === '-') {
        return value.slice(0, 10);
      }
      if (value.includes('T')) {
        return value.split('T')[0];
      }
      return value;
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    return date.toISOString().split('T')[0];
  }

  private coalesce<T>(value: T | null | undefined, fallback: T): T {
    if (value === null || value === undefined) {
      return fallback;
    }
    if (typeof value === 'string' && value.trim() === '') {
      return fallback;
    }
    return value;
  }

  async loadBrands(): Promise<void> {
    try {
      this.brands = await this.rewardsAdminService.getBrands();
    } catch (error) {
      console.error('Error loading brands', error);
    }
  }

  async loadCampaign(): Promise<void> {
    const id = this.dialogData?.campaignId || this.dialogData?.campaign?.id || this.route.snapshot.paramMap.get('id') || 'new';
    if (this.dialogData?.campaign) {
      this.campaign = this.dialogData.campaign;
      if (!this.campaign.id && this.campaign.campaignId) {
        this.campaign.id = this.campaign.campaignId;
      }
      this.form.patchValue({
        campaignName: this.campaign.campaignName,
        brandId: this.campaign.brandId,
        startAt: this.normalizeDate(this.campaign.startAt),
        endAt: this.normalizeDate(this.campaign.endAt),
        startingBalance: this.campaign.startingBalance,
        currentBalance: this.campaign.currentBalance,
        lowBalance: this.campaign.lowBalance,
        rules: this.parseJsonValue(this.campaign.rules),
      });
    }
    if (id === 'new') {
      return;
    }
    try {
      this.campaign = await this.rewardsAdminService.getCampaignById(id);
      if (!this.campaign.id && this.campaign.campaignId) {
        this.campaign.id = this.campaign.campaignId;
      }
      const current = this.form.getRawValue();
      this.form.patchValue({
        campaignName: this.coalesce(this.campaign.campaignName, current.campaignName),
        brandId: this.coalesce(this.campaign.brandId, current.brandId),
        startAt: this.coalesce(this.normalizeDate(this.campaign.startAt), current.startAt),
        endAt: this.coalesce(this.normalizeDate(this.campaign.endAt), current.endAt),
        startingBalance: this.coalesce(this.campaign.startingBalance, current.startingBalance),
        currentBalance: this.coalesce(this.campaign.currentBalance, current.currentBalance),
        lowBalance: this.coalesce(this.campaign.lowBalance, current.lowBalance),
        rules: this.coalesce(this.parseJsonValue(this.campaign.rules), current.rules),
      });
    } catch (error) {
      console.error('Error loading campaign', error);
    }
  }

  toJsonValue(value: any): any {
    if (typeof value === 'string') {
      try {
        return JSON.parse(value);
      } catch {
        return value;
      }
    }
    return value;
  }

  async save(): Promise<void> {
    if (this.form.invalid) {
      return;
    }
    this.saving = true;
    const payload: any = {
      campaignName: this.form.get('campaignName')?.value,
      brandId: this.form.get('brandId')?.value,
      startAt: this.form.get('startAt')?.value,
      endAt: this.form.get('endAt')?.value,
      startingBalance: this.form.get('startingBalance')?.value,
      currentBalance: this.form.get('currentBalance')?.value,
      lowBalance: this.form.get('lowBalance')?.value,
      rules: this.toJsonValue(this.form.get('rules')?.value),
    };
    if (!await this.confirmAllBrandsCampaign(payload)) {
      this.saving = false;
      return;
    }
    try {
      if (this.campaign) {
        const campaignId =
          this.campaign.id ||
          this.campaign.campaignId ||
          this.dialogData?.campaignId;
        payload.id = campaignId;
        if (campaignId) {
          this.campaign.id = campaignId;
        }
        await this.rewardsAdminService.putCampaign(payload);
        this.snackBar.open('Campaign successfully updated', 'Close', { duration: 2000 });
      } else {
        await this.rewardsAdminService.postCampaign(payload);
        this.snackBar.open('Campaign successfully created', 'Close', { duration: 2000 });
      }
      if (this.dialogRef) {
        this.dialogRef.close(true);
      } else {
        this.router.navigate(['/superadmin/rewards/campaigns']).then();
      }
    } catch (error) {
      console.error('Error saving campaign', error);
    } finally {
      this.saving = false;
    }
  }

  async delete(): Promise<void> {
    if (!this.campaign) {
      return;
    }
    const confirm = await this.uiAlertService.presentAlertConfirm('Delete this campaign?');
    if (!confirm) {
      return;
    }
    try {
      await this.rewardsAdminService.deleteCampaign(this.campaign);
      this.snackBar.open('Campaign successfully deleted', 'Close', { duration: 2000 });
      if (this.dialogRef) {
        this.dialogRef.close(true);
      } else {
        this.router.navigate(['/superadmin/rewards/campaigns']).then();
      }
    } catch (error) {
      console.error('Error deleting campaign', error);
    }
  }

  cancel(): void {
    if (this.dialogRef) {
      this.dialogRef.close(false);
    } else {
      this.router.navigate(['/superadmin/rewards/campaigns']).then();
    }
  }

  private confirmAllBrandsCampaign(campaign: any): Promise<boolean> {
    if (!rewardCampaignAppliesToAllBrands(campaign?.rules)) {
      return Promise.resolve(true);
    }

    return this.uiAlertService.presentAlertConfirm(
      'No brands are defined in the Rules JSON, so this campaign will apply to all brands. Are you sure you want to do this?',
    );
  }
}
